/**
 * 工作台部门大卡(B2,终稿屏①「部门卡片墙」):身份色瓦片 + 定位语 + 场景 minis 带 +
 * 能力 chips + 积分标 + 进入部门。
 *
 * ★2026-09-20:形制搬进 `WorkbenchCabinShell`(与外链舱富卡共用同一段 DOM / 同一套 .wb-*)。
 *   本文件只留「dept 舱独有的那几件事」:按权限现算场景数、积分标走价目表、落点是本站 hub。
 *   外壳统一之后,内容群卡墙上六张卡才是真的同一种卡(老板 0920:「还没有跟其他的统一」)。
 */
import { usePriceBook } from "@/entities/pricing/model/usePriceBook";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

import { SCENE_CARDS } from "@/entities/scene-card/catalog";
import { POSTER_HIDDEN_CARD_KEYS } from "@/entities/scene-card/model/posterPurpose";
import { deptFigureOf } from "@/entities/scene-card/model/figures";
import { useModuleVisible } from "@/features/auth";
import { useActiveSkin } from "@/shared/hooks/useTheme";
import { WorkbenchCabinShell } from "./WorkbenchCabinShell";
import { type DeptEntry } from "./workbenchCatalog";

interface Props {
  dept: DeptEntry;
  index: number;
  onOpen: () => void;
}

export function WorkbenchDeptCard({ dept, index, onOpen }: Props) {
  const { book } = usePriceBook();
  const costLabel = dept.costLabel(book);
  const { t } = useTranslation();
  // 部门双人图:女主前 / 男副后(情报部门反过来),切皮肤自动换人
  const skin = useActiveSkin();
  /**
   * ★场景数按**权限**现算,不用常量。
   * 跨模块导流卡(追爆 / 仿拍 / 从爆款开始)带 moduleKey,没有情报权限的用户在 hub 卡墙里
   * 根本看不到它们 —— 副标若数全量,就成了「部门卡说 4 个场景、点进去只有 2 张」。
   * 与 VideoHubSections / DhHubSections 的 gateOk 是同一把门(codex review P2 同类)。
   *
   * ★同理排掉 `funnel` 来源卡(波1):视频舱的追爆/仿拍已从卡墙下移到「换个来源」区,
   * 它们是「从哪起手」的入口而不是「这个舱能出的产物」。不排会变成
   * 「部门卡说 10 个场景、点进去卡墙只有 8 张」—— 同一个坑的第二次(codex review P3)。
   */
  const explosiveVisible = useModuleVisible("ai_explosive_parse");
  const hiddenCards = new Set<string>(POSTER_HIDDEN_CARD_KEYS);
  const sceneCount = SCENE_CARDS.filter(
    (c) =>
      c.hub === dept.hub &&
      c.enabled &&
      !c.funnel &&
      // ★2026-08-12(同一个坑第三次):海报舱把 7 张卡收进 POSTER_HIDDEN_CARD_KEYS
      //   (6 张长图用途 + 横宽宽图),卡墙只剩 7 张;不排就成了「说 14 个场景、
      //   点进去只有 7 张」。放回来时把 key 挪出那张表,这里自动跟。
      !hiddenCards.has(c.key) &&
      (!c.moduleKey || c.moduleKey !== "ai_explosive_parse" || explosiveVisible)
  ).length;

  return (
    <WorkbenchCabinShell
      cabinKey={dept.moduleKey}
      legacyAttrName="data-dept-card"
      index={index}
      chart={dept.chart}
      icon={dept.icon}
      title={t(dept.titleKey)}
      // ★副标拼成**一个**字符串再进外壳:外壳那一行是 flex(外链舱要在副标后挂「独立站」标),
      //   拆成两个文本节点会各自成为一个 flex item,中间被 gap 挤开一道 6px 的缝。
      tagline={`${dept.tagline}${sceneCount > 0 ? ` · ${sceneCount} 个场景` : ""}`}
      desc={dept.desc}
      minis={dept.minis}
      chips={dept.chips}
      figure={{ url: deptFigureOf(skin, dept.hub) }}
      footNote={
        costLabel || <span className="inline-block h-3 w-28 animate-pulse rounded bg-muted/60" />
      }
      footCta="进入部门"
      footCtaIcon={ArrowRight}
      onOpen={onOpen}
    />
  );
}
